import React, { useCallback, useState } from 'react';
import { Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { AxiosResponse } from 'axios';
import { debounce } from 'lodash';
import { twMerge } from 'tailwind-merge';
import {
  IProduct,
  ISearchProductReq,
  ISearchProductRes,
} from '@/interfaces/product';
import { searchProducts } from '@/adapters/product';

interface IProps {
  value?: number;
  product?: IProduct;
  size?: SizeType;
  isAll?: boolean;
  handleSelectData?: (product: IProduct) => void;
  readonly?: boolean;
  customClass?: string;
  allowClear?: boolean;
  search?: string;
}

const _criteria: ISearchProductReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const ProductSelector = ({
  value,
  size = 'large',
  handleSelectData,
  isAll = false,
  readonly = false,
  product,
  customClass,
  allowClear = true,
  search,
}: IProps) => {
  const [criteria, setCriteria] = useState<ISearchProductReq>({
    ..._criteria,
  });

  const { data: products, isLoading } = useQuery<
    AxiosResponse<ISearchProductRes>,
    Error
  >({
    queryKey: ['search-product', criteria, search],
    queryFn: async () =>
      await searchProducts({ ...criteria, search: criteria.search || search }),
  });

  let groupOptions =
    products?.data?.data?.map(g => {
      return {
        value: g.id,
        label: `${g.code} - ${g.name}`,
      };
    }) || [];

  const allOptions = [{ value: '', label: 'ทั้งหมด' }, ...groupOptions];

  const debouncedSearch = useCallback(
    debounce(value => {
      setCriteria({
        ...criteria,
        search: value,
      });
    }, 300),
    [],
  );

  const onChange = (value: number) => {
    const customerInfo = products?.data?.data?.find(d => d.id == value);
    handleSelectData && handleSelectData(customerInfo as IProduct);
  };

  const initOptions = {
    value: product?.id || 0,
    label: `${product?.code} - ${product?.name}`,
  };

  if (product) groupOptions = [...groupOptions, initOptions];
  return (
    <Select
      allowClear={allowClear}
      showSearch
      onSearch={debouncedSearch}
      loading={isLoading}
      value={value}
      className={twMerge('md:min-w-[150px] w-full', customClass)}
      size={size}
      placeholder="เลือกสินค้า"
      onChange={!handleSelectData ? undefined : value => onChange(value)}
      onClear={() => onChange(0)}
      options={isAll ? allOptions : [...groupOptions]}
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      disabled={readonly}
    />
  );
};

export default ProductSelector;
